import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  storageKeys,
  saveSessionItem,
  loadSessionItem,
  removeSessionItem,
  removeItem,
  loadItem,
  saveItem,
} from "../storage/index";
import { isAccountOnboardingComplete } from "../../features/auth/onboardingStatus";
import { clearOnboardingResumePath } from "../../features/onboarding/onboardingSession";
import {
  clearStaleLocalProfileArtifacts,
  syncOwnProfileFromServer,
} from "../../features/profile/profileModel";
import { DEFAULT_ROLE } from "../constants/roles";
import { apiGet, apiPost, setAuthToken } from "../../api/client";
import {
  isPreviewAuthEnabled,
  isPreviewAuthToken,
  PREVIEW_AUTH_TOKEN,
} from "../../features/auth/previewAuth";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(DEFAULT_ROLE);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credits, setCredits] = useState(0);
  const [shouldShowRoleSelection, setShouldShowRoleSelection] = useState(false);
  const [isRestoring, setIsRestoring] = useState(true);

  const clearSession = useCallback(() => {
    setUser(null);
    setRole(DEFAULT_ROLE);
    setIsAuthenticated(false);
    setCredits(0);
    setShouldShowRoleSelection(false);
    setAuthToken(null);
    removeSessionItem(storageKeys.user);
    removeSessionItem(storageKeys.role);
    removeSessionItem(storageKeys.credits);
    removeSessionItem(storageKeys.accessToken);
    // Keep completed profile + onboarding flag across auth refresh failures / reload.
    const hasProfile = loadItem(storageKeys.profile);
    const onboardingDone = loadItem(storageKeys.onboardingComplete);
    if (!hasProfile && !onboardingDone) {
      removeItem(storageKeys.onboardingDraft);
      removeItem(storageKeys.onboardingComplete);
      clearOnboardingResumePath();
    }
  }, []);

  const applySession = useCallback((userData, userRole, userCredits) => {
    const resolvedRole = userRole || userData?.role || DEFAULT_ROLE;
    const resolvedCredits = userCredits ?? userData?.credits ?? 0;

    setUser(userData);
    setRole(resolvedRole);
    setIsAuthenticated(true);
    setCredits(resolvedCredits);
    saveSessionItem(storageKeys.user, userData);
    saveSessionItem(storageKeys.role, resolvedRole);
    saveSessionItem(storageKeys.credits, resolvedCredits);

    if (userData?.onboardingComplete || isAccountOnboardingComplete(userData)) {
      saveItem(storageKeys.onboardingComplete, true);
      clearStaleLocalProfileArtifacts();
      syncOwnProfileFromServer(userData.id).catch(() => {});
    }
  }, []);

  useEffect(() => {
    async function restoreSession() {
      try {
        const savedToken = loadSessionItem(storageKeys.accessToken);

        if (isPreviewAuthEnabled() && isPreviewAuthToken(savedToken)) {
          const previewUser = loadSessionItem(storageKeys.user);
          if (previewUser) {
            setAuthToken(savedToken);
            applySession(previewUser, previewUser.role, previewUser.credits);
            return;
          }
        }

        if (savedToken) {
          setAuthToken(savedToken);
          try {
            const data = await apiGet("/auth/me");
            applySession(data.user, data.user.role, data.user.credits);
            if (isAccountOnboardingComplete(data.user, data.profile)) {
              saveItem(storageKeys.onboardingComplete, true);
            } else if (data.user?.onboardingComplete === false && !data.profile) {
              removeItem(storageKeys.onboardingComplete);
            }
            return;
          } catch {
            // fall through to refresh
          }
        }

        try {
          const data = await apiPost("/auth/refresh");
          setAuthToken(data.accessToken);
          saveSessionItem(storageKeys.accessToken, data.accessToken);
          applySession(data.user, data.role || data.user?.role, data.user?.credits);
          if (isAccountOnboardingComplete(data.user)) {
            saveItem(storageKeys.onboardingComplete, true);
          } else if (data.user?.onboardingComplete === false) {
            removeItem(storageKeys.onboardingComplete);
          }
        } catch {
          clearSession();
        }
      } finally {
        setIsRestoring(false);
      }
    }

    restoreSession();
  }, [applySession, clearSession]);

  useEffect(() => {
    const onTokenRefreshed = (event) => {
      const { accessToken, user: refreshedUser } = event.detail;
      saveSessionItem(storageKeys.accessToken, accessToken);
      if (refreshedUser) {
        applySession(refreshedUser, refreshedUser.role, refreshedUser.credits);
      }
    };

    const onSessionExpired = () => {
      clearSession();
    };

    window.addEventListener("auth:token-refreshed", onTokenRefreshed);
    window.addEventListener("auth:session-expired", onSessionExpired);

    return () => {
      window.removeEventListener("auth:token-refreshed", onTokenRefreshed);
      window.removeEventListener("auth:session-expired", onSessionExpired);
    };
  }, [applySession, clearSession]);

  const refreshUser = useCallback(async () => {
    try {
      const data = await apiGet("/auth/me");
      applySession(data.user, data.user.role, data.user.credits);
      if (isAccountOnboardingComplete(data.user, data.profile)) {
        saveItem(storageKeys.onboardingComplete, true);
        clearOnboardingResumePath();
      } else if (data.user?.onboardingComplete === false && !data.profile) {
        removeItem(storageKeys.onboardingComplete);
      }
      return data.user;
    } catch {
      return null;
    }
  }, [applySession]);

  const login = (userData, userRole = DEFAULT_ROLE, isNewUser = false, accessToken = null) => {
    if (accessToken) {
      setAuthToken(accessToken);
      saveSessionItem(storageKeys.accessToken, accessToken);
    }

    applySession(userData, userRole, userData?.credits);

    if (userData?.onboardingComplete || isAccountOnboardingComplete(userData)) {
      saveItem(storageKeys.onboardingComplete, true);
      clearOnboardingResumePath();
    } else if (isNewUser) {
      removeItem(storageKeys.onboardingComplete);
    }
  };

  const logout = async () => {
    if (!isPreviewAuthToken(loadSessionItem(storageKeys.accessToken))) {
      try {
        await apiPost("/auth/logout");
      } catch {
        // ignore logout errors
      }
    }

    clearSession();
  };

  const updateRole = (newRole) => {
    setRole(newRole);
    saveSessionItem(storageKeys.role, newRole);
    saveSessionItem(storageKeys.roleSelectionSeen, true);
    setShouldShowRoleSelection(false);
  };

  const markRoleSelectionSeen = () => {
    saveSessionItem(storageKeys.roleSelectionSeen, true);
    setShouldShowRoleSelection(false);
  };

  const markRoleSelectionSkipped = () => {
    saveSessionItem(storageKeys.roleSelectionSkipped, true);
    saveSessionItem(storageKeys.roleSelectionSeen, true);
    setShouldShowRoleSelection(false);
  };

  const updateCredits = (newCredits) => {
    setCredits(newCredits);
    saveSessionItem(storageKeys.credits, newCredits);
  };

  const addCredits = (amount) => {
    const newTotal = credits + amount;
    updateCredits(newTotal);
    return newTotal;
  };

  const deductCredits = (amount) => {
    if (credits >= amount) {
      const newTotal = credits - amount;
      updateCredits(newTotal);
      return newTotal;
    }
    return null;
  };

  const value = {
    user,
    role,
    isAuthenticated,
    credits,
    isRestoring,
    shouldShowRoleSelection,
    login,
    logout,
    refreshUser,
    updateRole,
    updateCredits,
    addCredits,
    deductCredits,
    markRoleSelectionSeen,
    markRoleSelectionSkipped,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
